import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Grid, TextField, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Switch, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, useTheme, Snackbar, Alert, Stack } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon } from '@mui/icons-material';
import { deleteData, getData, postData, putData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import Loader from '../../../common/spinner';

const LeaveTypesManagement = () => {
    const theme = useTheme();
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const orgId = userData?.organization_id;
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [currentLeaveType, setCurrentLeaveType] = useState({
        leave_type: '',
        description: '',
        max_days: 0,
        is_active: true
    });

    const getLeaveTypeColor = (leaveTypeName) => {
        const colors = [
            '#2196F3', '#F44336', '#4CAF50', '#9C27B0', '#FF9800',
            '#795548', '#673AB7', '#00BCD4', '#E91E63', '#607D8B'
        ];
        const index = leaveTypeName.length % colors.length;
        return colors[index];
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message: typeof message === 'string' ? message : 'An error occurred',
            severity
        });
    };

    const fetchLeaveTypes = useCallback(async () => {
        if (!orgId) return;
        setLoading(true);
        try {
            const response = await getData(API_ENDPOINTS.fetchLeaveCountsForOrganization(orgId));
            if (response.status === 'SUCCESS') {
                const types = response.message?.leaveTypes || [];
                setLeaveTypes(types.map(lt => ({
                    ...lt,
                    color: getLeaveTypeColor(lt.leave_type)
                })));
            }
        } catch (error) {
            showSnackbar('Failed to load leave types', 'error');
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        fetchLeaveTypes();
    }, [fetchLeaveTypes]);

    // Handlers
    const handleOpenDialog = (leaveType = null) => {
        if (leaveType) {
            setCurrentLeaveType({ ...leaveType });
            setEditMode(true);
        } else {
            setCurrentLeaveType({
                leave_type: '',
                description: '',
                max_days: 0,
                is_active: true
            });
            setEditMode(false);
        }
        setDialogOpen(true);
    };

    const handleSaveLeaveType = async () => {
        if (!currentLeaveType.leave_type.trim()) {
            return showSnackbar('Leave type name is required', 'error');
        }

        try {
            let response;
            if (editMode) {
                const payload = {
                    leave_type: currentLeaveType.leave_type,
                    description: currentLeaveType.description,
                    max_days: currentLeaveType.max_days,
                    is_active: currentLeaveType.is_active
                };
                response = await putData(API_ENDPOINTS.updateLeaveTypeById(currentLeaveType.id), payload);
            } else {
                const payload = {
                    organization_id: parseInt(orgId),
                    leave_type: currentLeaveType.leave_type,
                    description: currentLeaveType.description,
                    max_days: currentLeaveType.max_days
                };
                response = await postData(API_ENDPOINTS.createLeaveType, payload);
            }

            if (response.status === 'SUCCESS') {
                showSnackbar(`Leave type ${editMode ? 'updated' : 'created'} successfully`);
                fetchLeaveTypes();
                setDialogOpen(false);
            }
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to save leave type', 'error');
        }
    };

    const handleDeleteLeaveType = async () => {
        try {
            const response = await deleteData(API_ENDPOINTS.deleteLeaveTypeById(currentLeaveType.id));
            if (response.status === 'SUCCESS') {
                showSnackbar('Leave type deleted successfully');
                fetchLeaveTypes();
                setDeleteDialogOpen(false);
            }
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to delete leave type', 'error');
        }
    };

    return (
        <Box sx={{ p: 1 }}>
            {loading && <Loader />}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Leave Types Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ borderRadius: 1, textTransform: 'none', px: 3 }}
                >
                    Add Leave Type
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.12)', borderRadius: 2 }}>
                <Table>
                    <TableHead sx={{ bgcolor: theme.palette.grey[50] }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Max Days</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {leaveTypes.length > 0 ? (
                            leaveTypes.map((leaveType) => (
                                <TableRow key={leaveType.id} hover>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: leaveType.color }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{leaveType.leave_type}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 300 }}>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {leaveType.description || 'No description'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{leaveType.max_days} days</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={leaveType.is_active ? 'Active' : 'Inactive'}
                                            color={leaveType.is_active ? 'success' : 'default'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenDialog(leaveType)} color="primary">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => { setCurrentLeaveType(leaveType); setDeleteDialogOpen(true); }} color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                    <Typography variant="body2" color="text.secondary">No leave types found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add / Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle component="div" sx={{ bgcolor: theme.palette.primary.main, color: 'white', py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6">
                            {editMode ? `Edit ${currentLeaveType.leave_type}` : 'Add New Leave Type'}
                        </Typography>
                        <IconButton onClick={() => setDialogOpen(false)} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ mt: 3 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Leave Type Name"
                                value={currentLeaveType.leave_type}
                                onChange={(e) => setCurrentLeaveType({ ...currentLeaveType, leave_type: e.target.value })}
                                required
                                sx={{ mb: 2, mt: 1 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Maximum Days Per Year"
                                value={currentLeaveType.max_days}
                                onChange={(e) => setCurrentLeaveType({ ...currentLeaveType, max_days: parseFloat(e.target.value) || 0 })}
                                sx={{ mb: 2 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Description"
                                value={currentLeaveType.description}
                                onChange={(e) => setCurrentLeaveType({ ...currentLeaveType, description: e.target.value })}
                                sx={{ mb: 2 }}
                            />
                        </Grid>
                        {editMode && (
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={currentLeaveType.is_active}
                                            onChange={(e) => setCurrentLeaveType({ ...currentLeaveType, is_active: e.target.checked })}
                                            color="success"
                                        />
                                    }
                                    label="Active Status"
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setDialogOpen(false)} variant="outlined" sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleSaveLeaveType} variant="contained" sx={{ textTransform: 'none' }}>
                        {editMode ? 'Update' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{ bgcolor: theme.palette.error.main, color: 'white' }}>Confirm Delete</DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography variant="body1">
                        Are you sure you want to delete <strong>{currentLeaveType.leave_type}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteLeaveType} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LeaveTypesManagement;